// React imports
import useFormatCurrencyForSrp from '@/src/hooks/currency/useFormatCurrencyForSrp'
import { FlightRouteInfo, FullBaggageResponse, MiniRuleData } from '@/src/types/flight'
import { TranslationFunction } from '@/src/types/translations'
import { convertDDMMYYYYToISO } from '@/src/utils/date/convertDateFormat'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FareRuleDetails, ProcessedBaggageData, TabItem } from './FareRuleDetails'

interface FareRulesWrapperProps {
    baggageData: FullBaggageResponse | undefined
    baggageLoading: boolean
    baggageError: string | null
    miniruleData: MiniRuleData | undefined
    miniRuleLoading: boolean
    miniRuleError: string | null
    targetSectors?: FlightRouteInfo[]
    formatDate: (date: string, fmt: string) => string
    tCommon: TranslationFunction<'common'>
    showBaggage?: boolean
    retryCount?: number
    handleRetryBaggage?: () => void
    maxRetries?: number
}

const getDeptStr = (sectors: FlightRouteInfo[] | undefined, activeTab: string): string => {
    if (!sectors || sectors.length === 0 || !activeTab) return ''
    const [departure, arrival] = activeTab.split(' → ')
    const sector =
        sectors.find(s => s.firstDeparture.airportCode === departure && s.lastArrival.airportCode === arrival) ||
        sectors[0]
    const firstSegment = sector.flights.segments[0]
    if (!firstSegment) return ''
    const formattedDate = convertDDMMYYYYToISO(firstSegment.departure.date)
    return [
        firstSegment.departure.airportCode,
        firstSegment.arrival.airportCode,
        formattedDate,
        firstSegment.departure.time || ''
    ].join('_')
}

const FareRulesWrapper: React.FC<FareRulesWrapperProps> = ({
    baggageData,
    baggageLoading,
    baggageError,
    miniruleData,
    miniRuleLoading,
    miniRuleError,
    targetSectors,
    formatDate,
    tCommon,
    showBaggage,
    retryCount = 0,
    handleRetryBaggage,
    maxRetries = 2
}) => {
    const { formatCurrency } = useFormatCurrencyForSrp()
    const processedTabs = useMemo((): TabItem[] => {
        if (!targetSectors || targetSectors.length === 0) {
            return []
        }
        return targetSectors.map((sector, index) => ({
            label: `${sector.firstDeparture.airportCode} → ${sector.lastArrival.airportCode}`,
            key: `${sector.firstDeparture.airportCode}-${sector.lastArrival.airportCode}-${sector.firstDeparture.timestamp}-${index}`
        }))
    }, [targetSectors])

    const [activeTab, setActiveTab] = useState(() => (processedTabs.length > 0 ? processedTabs[0].label : ''))

    useEffect(() => {
        if (processedTabs.length > 0 && !processedTabs.some(tab => tab.label === activeTab)) {
            setActiveTab(processedTabs[0].label)
        }
    }, [processedTabs, activeTab])

    const handleTabChange = useCallback((tab: string) => {
        setActiveTab(tab)
    }, [])

    const deptStr = useMemo(() => getDeptStr(targetSectors, activeTab), [targetSectors, activeTab, formatDate])

    const processedBaggageData = useMemo((): ProcessedBaggageData => {
        const baggage = baggageData?.BI?.[deptStr]?.bg?.ADT
        return {
            checkIn: baggage?.cib ? `${baggage.cib}` : null,
            cabin: baggage?.cab ? `${baggage.cab}` : null
        }
    }, [baggageData, deptStr])

    if (processedTabs.length === 0) {
        return null
    }

    return (
        <>
            <FareRuleDetails
                processedBaggageData={processedBaggageData}
                processedTabs={processedTabs}
                activeTab={activeTab}
                isLoading={baggageLoading}
                error={baggageError}
                onTabChange={handleTabChange}
                t={tCommon}
                miniruleData={miniruleData}
                miniRuleLoading={miniRuleLoading}
                miniRuleerror={miniRuleError}
                showBaggage={showBaggage}
                formatCurrency={formatCurrency}
                retryCount={retryCount}
                maxRetries={maxRetries}
                onRetryBaggage={handleRetryBaggage}
            />
        </>
    )
}

export { FareRulesWrapper }
export default FareRulesWrapper
